'use client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import type {
  TRPCAppRouter as AppRouter,
  RouterInputs,
  RouterOutputs,
} from '@repo/backend-types/trpc-api';
export type { RouterInputs, RouterOutputs } from '@repo/backend-types/trpc-api';
import {
  createTRPCClient,
  httpLink,
  httpBatchLink,
  httpSubscriptionLink,
  splitLink,
  TRPCClientError,
  TRPCClientErrorLike,
  type TRPCLink,
} from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { ReactNode, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { transformer } from '@repo/api/transformer';
import { useBullhornSessionContext } from '@/api/BullhornSession';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { redirectToLoginPage } from '@/api/auth/authUtils';
import { useToroExtensionVersion } from '@repo/ui/components/ToroExtensionContext';
export {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  useMutation,
  skipToken,
  keepPreviousData,
} from '@tanstack/react-query';
export { useSubscription } from '@trpc/tanstack-react-query';

const trpcContext = createTRPCContext<AppRouter>();
export const { TRPCProvider, useTRPC, useTRPCClient } = trpcContext;

export type ResponseError = TRPCClientErrorLike<AppRouter>;

// Deduplicates concurrent 401 redirects so a burst of failing queries only fires one navigation.
let isRedirectingToSignIn = false;

// Redirects to sign-in on any tRPC 401
const authErrorLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next: (value) => observer.next(value),
        error: async (err: TRPCClientError<AppRouter>) => {
          try {
            const httpStatus = err.data?.httpStatus;
            if (httpStatus === 401 && typeof window !== 'undefined' && !isRedirectingToSignIn) {
              isRedirectingToSignIn = true;
              await redirectToLoginPage(err);
            }
          } finally {
            observer.error(err);
          }
        },
        complete: () => observer.complete(),
      });
      return unsubscribe;
    });
  };
};

export function TrpcWrapper({ children, apiUrl }: { children: ReactNode; apiUrl: string }) {
  const queryClient = useQueryClient();
  const { session } = useBullhornSessionContext();
  const sessionToken = session?.sessionToken;
  const extensionVersion = useToroExtensionVersion();

  // TODO: look into creating a new client for server-side components per the docs
  const trpcClient = useMemo(() => {
    // Include Authorization header for embedded sessions to work around 3rd party cookie blocking
    const headers: Record<string, string> = {
      ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
      ...(extensionVersion && { 'toro-extension-version': extensionVersion }),
    };

    return createTRPCClient<AppRouter>({
      links: [
        authErrorLink,
        splitLink({
          condition: (op) => op.type === 'subscription',
          true: httpSubscriptionLink({
            url: `${apiUrl}/trpc`,
            transformer,
            // Use EventSourcePolyfill to support custom headers (native EventSource doesn't support headers)
            // This is needed for embedded sessions when 3rd party cookies are blocked
            EventSource: EventSourcePolyfill,
            eventSourceOptions: () => ({
              headers,
            }),
          }),
          false: httpLink({
            url: `${apiUrl}/trpc`,
            transformer,
            headers,
          }),
        }),
      ],
    });
  }, [apiUrl, sessionToken, extensionVersion]);

  return (
    <TRPCProvider queryClient={queryClient} trpcClient={trpcClient}>
      {children}
    </TRPCProvider>
  );
}
